import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { WorkStationConfiguration } from '../configurations/work-station-configuration'
import { PropertyCache } from '../../../properties/property-cache'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { switchMap } from 'rxjs/operators'
import {
  IWsConfigurationDialogOptions,
  WsConfigurationDialogComponent,
} from './ws-configuration-dialog/ws-configuration-dialog.component'
import { ConstraintUtils } from '../../../process/capability-spec/constraints/constraint-utils'
import { Property, PropertyType } from '../../../properties/property'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { PropertyInputComponent } from '../../../properties/property-input/property-input.component'
import { AsyncPipe } from '@angular/common'
import { MatTooltip } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  ElderDataToolbarComponent,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
} from '@elderbyte/ngx-starter'

@Component({
  selector: 'app-work-station-configuration-browser',
  templateUrl: './work-station-configuration-browser.component.html',
  styleUrls: ['./work-station-configuration-browser.component.scss'],
  providers: [PropertyCache],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    MatTooltip,
    PropertyInputComponent,
    ElderStopEventPropagationDirective,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkStationConfigurationBrowserComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _wsConfigurations$ = new BehaviorSubject<WorkStationConfiguration[]>([])
  public readonly configurationsPopulated$: Observable<WorkStationConfiguration[]>
  private dialog = inject(MatDialog)
  private propertyCache = inject(PropertyCache)

  @Output()
  public configurationsChange: EventEmitter<WorkStationConfiguration[]> = new EventEmitter<
    WorkStationConfiguration[]
  >()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.configurationsPopulated$ = combineLatest([this._wsConfigurations$]).pipe(
      switchMap(([configs]) => this.populateConfigs(configs))
    )
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set configurations(configs: WorkStationConfiguration[]) {
    this._wsConfigurations$.next(configs)
  }

  public get configurations(): WorkStationConfiguration[] {
    return this._wsConfigurations$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onConfigurationClicked(config: WorkStationConfiguration): void {
    const dialogRef = this.openDialog(config)
    dialogRef.afterClosed().subscribe((updatedConf: WorkStationConfiguration) => {
      if (updatedConf) {
        const confToUpdate = this.configurations.find((c) => c.spec.id === updatedConf.spec.id)
        ConstraintUtils.update(updatedConf?.spec, confToUpdate?.spec)
        confToUpdate.possibleValues = []
        ConstraintUtils.updateConf(updatedConf?.activeConf, confToUpdate?.activeConf)
        confToUpdate.activeConf.values = []
        this.configurationsChange.emit(this.configurations)
      }
    })
  }

  public addConfiguration(): void {
    const dialogRef = this.openDialog(new WorkStationConfiguration())
    dialogRef.afterClosed().subscribe((config: WorkStationConfiguration) => {
      if (config) {
        const configurations = [...this.configurations]
        configurations.push(config)
        this.configurations = configurations
        this.configurationsChange.emit(this.configurations)
      }
    })
  }

  public onRemoveConfiguration(toRemove: WorkStationConfiguration[]) {
    const configurations = [...this.configurations]
    toRemove.forEach((c) => configurations.splice(configurations.indexOf(c), 1))
    this.configurations = configurations
    this.configurationsChange.emit(this.configurations)
  }

  public canSetPossibleValues(configuration: WorkStationConfiguration): boolean {
    switch (configuration?.spec?.property?.type) {
      case PropertyType.VALUE_LIST:
      case PropertyType.VALUE_ENUMERATION:
      case PropertyType.QUANTITY_LIST:
      case PropertyType.QUANTITY_ENUMERATION:
        return true
      default:
        return false
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private populateConfigs(
    configs: WorkStationConfiguration[]
  ): Observable<WorkStationConfiguration[]> {
    if (configs.length === 0) {
      return of([])
    }
    return this.propertyCache.populateObjects(
      configs,
      (conf: WorkStationConfiguration) => conf.spec.propertyId,
      (conf: WorkStationConfiguration, prop: Property) => (conf.spec.property = prop)
    )
  }

  private openDialog(
    config: WorkStationConfiguration
  ): MatDialogRef<WsConfigurationDialogComponent> {
    return this.dialog.open(WsConfigurationDialogComponent, {
      minWidth: '30vw',
      height: '30vh',
      data: <IWsConfigurationDialogOptions>{
        config: config,
      },
    })
  }
}
